import { RotateCcw, Trash2 } from "lucide-react"
import { useIntl } from "react-intl"
import { CHOICE_LETTERS } from "@/components/quizzes/format"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Radio } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { ContentAreaGroup } from "@/types/flashcards"
import type { DraftCard, DraftQuestion } from "@/types/generations"

export type ReviewItem<T> = T & { removed: boolean }

/** One editable AI draft (a card or a question) on the review screen. */
export function DraftEditor({
  index,
  item,
  contentAreaGroups,
  onChange,
}: {
  index: number
  item: ReviewItem<DraftCard> | ReviewItem<DraftQuestion>
  contentAreaGroups: ContentAreaGroup[]
  onChange: (item: ReviewItem<DraftCard> | ReviewItem<DraftQuestion>) => void
}) {
  const intl = useIntl()
  const id = `draft-${index}`
  const isCard = "front" in item
  const label = isCard
    ? intl.formatMessage({ id: "generations.card_n" }, { number: index + 1 })
    : intl.formatMessage({ id: "generations.question_n" }, { number: index + 1 })

  return (
    <li
      className={cn(
        "list-none rounded-xl border border-hairline bg-page p-4",
        item.removed && "bg-surface",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3>
          {label}
          {item.removed && <span className="ml-2 text-sm font-normal text-ink-muted">{intl.formatMessage({ id: "generations.removed" })}</span>}
        </h3>
        {item.removed ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...item, removed: false })}>
            <RotateCcw className="h-4 w-4" /> {intl.formatMessage({ id: "generations.keep" })}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={
              isCard
                ? intl.formatMessage({ id: "generations.remove_card" }, { number: index + 1 })
                : intl.formatMessage({ id: "generations.remove_question" }, { number: index + 1 })
            }
            onClick={() => onChange({ ...item, removed: true })}
          >
            <Trash2 className="h-4 w-4" /> {intl.formatMessage({ id: "generations.remove" })}
          </Button>
        )}
      </div>

      {!item.removed && (
        <div className="mt-3 space-y-3">
          {isCard ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor={`${id}-front`}>{intl.formatMessage({ id: "generations.front" })}</label>
                <textarea
                  id={`${id}-front`}
                  className="form-control form-control-textarea min-h-20"
                  value={item.front}
                  onChange={(e) => onChange({ ...item, front: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor={`${id}-back`}>{intl.formatMessage({ id: "generations.back" })}</label>
                <textarea
                  id={`${id}-back`}
                  className="form-control form-control-textarea min-h-20"
                  value={item.back}
                  onChange={(e) => onChange({ ...item, back: e.target.value })}
                />
              </div>
            </div>
          ) : (
            <QuestionFields id={id} item={item as ReviewItem<DraftQuestion>} onChange={onChange} />
          )}

          {contentAreaGroups.some((g) => g.areas.length > 0) && (
            <div className="space-y-1.5">
              <label htmlFor={`${id}-area`}>{intl.formatMessage({ id: "generations.content_area" })}</label>
              <Select
                id={`${id}-area`}
                value={item.content_area_id ? String(item.content_area_id) : ""}
                onChange={(e) => onChange({ ...item, content_area_id: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">{intl.formatMessage({ id: "generations.none" })}</option>
                {contentAreaGroups.length === 1
                  ? areaOptions(contentAreaGroups[0])
                  : contentAreaGroups.map((group) => (
                      <optgroup key={group.label} label={group.label}>
                        {areaOptions(group)}
                      </optgroup>
                    ))}
              </Select>
            </div>
          )}
        </div>
      )}
    </li>
  )
}

function QuestionFields({
  id,
  item,
  onChange,
}: {
  id: string
  item: ReviewItem<DraftQuestion>
  onChange: (item: ReviewItem<DraftQuestion>) => void
}) {
  const intl = useIntl()
  return (
    <>
      <div className="space-y-1.5">
        <label htmlFor={`${id}-text`}>{intl.formatMessage({ id: "generations.question" })}</label>
        <textarea
          id={`${id}-text`}
          className="form-control form-control-textarea min-h-20"
          value={item.text}
          onChange={(e) => onChange({ ...item, text: e.target.value })}
        />
      </div>
      <fieldset className="space-y-2">
        <legend>{intl.formatMessage({ id: "generations.choices" })}</legend>
        {CHOICE_LETTERS.map((letter, i) => (
          <div key={letter} className="flex items-center gap-3">
            <Radio
              name={`${id}-correct`}
              aria-label={intl.formatMessage({ id: "generations.choice_correct" }, { letter })}
              checked={item.correct_choice === i}
              onChange={() => onChange({ ...item, correct_choice: i })}
            />
            <span className="w-4 text-sm font-medium text-ink-muted" aria-hidden>
              {letter}
            </span>
            <Input
              aria-label={intl.formatMessage({ id: "generations.choice" }, { letter })}
              value={item.choices[i] ?? ""}
              onChange={(e) =>
                onChange({ ...item, choices: item.choices.map((c, j) => (j === i ? e.target.value : c)) })
              }
            />
          </div>
        ))}
      </fieldset>
      <div className="space-y-1.5">
        <label htmlFor={`${id}-explanation`}>{intl.formatMessage({ id: "generations.explanation" })}</label>
        <textarea
          id={`${id}-explanation`}
          className="form-control form-control-textarea min-h-20"
          value={item.explanation}
          onChange={(e) => onChange({ ...item, explanation: e.target.value })}
        />
      </div>
    </>
  )
}

function areaOptions(group: ContentAreaGroup) {
  return group.areas.map((area) => (
    <option key={area.id} value={area.id}>
      {area.name}
    </option>
  ))
}
